import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Input,
  Button,
  Collapse,
  InputGroup,
  InputGroupText,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { personnelLeaveAPI, personnelAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import SideModal from '../../components/Common/SideModal';

interface LeaveRecord {
  id: string;
  date: string;
  type: string;
  notes: string | null;
  createdAt: string;
  personnel: {
    id: string;
    firstName: string;
    lastName: string;
    title: string | null;
    employmentType: string;
  };
  createdBy: { firstName: string; lastName: string };
}

const leaveTypeLabels: Record<string, string> = {
  IZIN: 'İzin',
  RAPOR: 'Rapor',
  DEVAMSIZLIK: 'Devamsızlık',
};

const leaveTypeColors: Record<string, string> = {
  IZIN: 'warning',
  RAPOR: 'info',
  DEVAMSIZLIK: 'danger',
};

const PersonnelLeaveList: React.FC = () => {
  const isMobile = useIsMobile();
  const [records, setRecords] = useState<LeaveRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [personnelList, setPersonnelList] = useState<any[]>([]);

  // Filters
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const firstDay = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const lastDayDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastDay = `${lastDayDate.getFullYear()}-${pad(lastDayDate.getMonth() + 1)}-${pad(lastDayDate.getDate())}`;
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [personnelFilter, setPersonnelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [isBulk, setIsBulk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    personnelId: '',
    date: '',
    startDate: '',
    endDate: '',
    type: 'IZIN',
    notes: '',
  });

  useEffect(() => {
    fetchPersonnel();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [startDate, endDate, personnelFilter, typeFilter, searchFilter]);

  const fetchPersonnel = async () => {
    try {
      const res = await personnelAPI.getAll({ isActive: true });
      setPersonnelList(res.data);
    } catch (error) {
      console.error('Personnel fetch error:', error);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await personnelLeaveAPI.getAll({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        personnelId: personnelFilter || undefined,
        type: typeFilter || undefined,
      });
      let filtered = res.data;
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        filtered = filtered.filter((r: LeaveRecord) =>
          `${r.personnel.firstName} ${r.personnel.lastName}`.toLowerCase().includes(s) ||
          (r.personnel.title && r.personnel.title.toLowerCase().includes(s)) ||
          (r.notes && r.notes.toLowerCase().includes(s))
        );
      }
      setRecords(filtered);
    } catch (error) {
      console.error('Leave records fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setPersonnelFilter('');
    setTypeFilter('');
    setSearchFilter('');
    setStartDate(firstDay);
    setEndDate(lastDay);
  };

  const hasActiveFilters = personnelFilter || typeFilter || searchFilter || startDate !== firstDay || endDate !== lastDay;

  const openAddModal = (bulk = false) => {
    setIsBulk(bulk);
    setFormData({ personnelId: '', date: '', startDate: '', endDate: '', type: 'IZIN', notes: '' });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.personnelId || !formData.type) {
      toast.error('Personel ve tür seçilmeli');
      return;
    }

    try {
      setSaving(true);
      if (isBulk) {
        if (!formData.startDate || !formData.endDate) {
          toast.error('Tarih aralığı seçilmeli');
          return;
        }
        const result = await personnelLeaveAPI.createBulk({
          personnelId: formData.personnelId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          type: formData.type,
          notes: formData.notes || undefined,
        });
        toast.success(`${result.data.created} gün izin kaydı oluşturuldu`);
      } else {
        if (!formData.date) {
          toast.error('Tarih seçilmeli');
          return;
        }
        await personnelLeaveAPI.create({
          personnelId: formData.personnelId,
          date: formData.date,
          type: formData.type,
          notes: formData.notes || undefined,
        });
        toast.success('İzin kaydı oluşturuldu');
      }
      setModalOpen(false);
      fetchRecords();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kayıt oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await personnelLeaveAPI.delete(id);
      toast.success('İzin kaydı silindi');
      fetchRecords();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">İzin Takip</h4>
            <div className="page-title-right d-flex gap-2">
              <Button color="primary" onClick={() => openAddModal(true)}>
                <i className="ri-calendar-line me-1"></i>
                Toplu Ekle
              </Button>
              <Button color="primary" onClick={() => openAddModal(false)}>
                <i className="ri-add-line me-1"></i>
                Yeni Kayıt
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">İzin Kayıtları</h5>
                </Col>
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Ad soyad ara..."
                          style={{ width: '150px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={personnelFilter}
                          onChange={(e) => setPersonnelFilter(e.target.value)}
                          style={{ minWidth: '180px' }}
                        >
                          <option value="">Tüm Personel</option>
                          {personnelList.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {p.firstName} {p.lastName}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Tüm Türler</option>
                          <option value="IZIN">İzin</option>
                          <option value="RAPOR">Rapor</option>
                          <option value="DEVAMSIZLIK">Devamsızlık</option>
                        </Input>
                      </Col>
                      <Col md="auto">
                        <InputGroup size="sm">
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                          <InputGroupText>-</InputGroupText>
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                        </InputGroup>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button color="light" size="sm" onClick={clearFilters} title="Filtreleri Temizle">
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[personnelFilter, typeFilter].filter(Boolean).length + (startDate !== firstDay || endDate !== lastDay ? 1 : 0)}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Ad soyad ara..."
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Personel</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={personnelFilter}
                          onChange={(e) => setPersonnelFilter(e.target.value)}
                        >
                          <option value="">Tüm Personel</option>
                          {personnelList.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {p.firstName} {p.lastName}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Tür</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                        >
                          <option value="">Tüm Türler</option>
                          <option value="IZIN">İzin</option>
                          <option value="RAPOR">Rapor</option>
                          <option value="DEVAMSIZLIK">Devamsızlık</option>
                        </Input>
                      </div>
                      <Row>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Başlangıç</label>
                            <Input type="date" bsSize="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Bitiş</label>
                            <Input type="date" bsSize="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                          </div>
                        </Col>
                      </Row>
                      {hasActiveFilters && (
                        <button className="btn btn-sm btn-outline-secondary w-100 mt-2" onClick={clearFilters}>
                          <i className="ri-close-line me-1"></i>Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Kayıt bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                <div className="admin-card-list">
                  {records.map((r) => (
                    <div key={r.id} className="admin-card-item">
                      <div className="admin-card-header">
                        <h6 className="admin-card-title">
                          {r.personnel.firstName} {r.personnel.lastName}
                        </h6>
                        <Badge color={leaveTypeColors[r.type] || 'secondary'}>
                          {leaveTypeLabels[r.type] || r.type}
                        </Badge>
                      </div>
                      <div className="admin-card-body">
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-calendar-line"></i>Tarih
                          </span>
                          <span className="admin-info-value">
                            {new Date(r.date).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        {r.notes && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-sticky-note-line"></i>Not
                            </span>
                            <span className="admin-info-value">{r.notes}</span>
                          </div>
                        )}
                      </div>
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={() => handleDelete(r.id)}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Sil
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Personel</th>
                        <th>Unvan</th>
                        <th>Tür</th>
                        <th>Not</th>
                        <th>Oluşturan</th>
                        <th className="text-center" style={{ width: '80px' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong>
                              {new Date(r.date).toLocaleDateString('tr-TR', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </strong>
                          </td>
                          <td>{r.personnel.firstName} {r.personnel.lastName}</td>
                          <td className="text-muted">{r.personnel.title || '-'}</td>
                          <td>
                            <Badge color={leaveTypeColors[r.type] || 'secondary'}>
                              {leaveTypeLabels[r.type] || r.type}
                            </Badge>
                          </td>
                          <td className="text-muted">{r.notes || '-'}</td>
                          <td className="text-muted fs-12">{r.createdBy.firstName} {r.createdBy.lastName}</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-light text-danger"
                              onClick={() => handleDelete(r.id)}
                              title="Sil"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add Leave SideModal */}
      <SideModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isBulk ? 'Toplu İzin Kaydı' : 'Yeni İzin Kaydı'}
        size="md"
      >
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            {/* Personel Bilgisi */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
              <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Personel Bilgisi
              </h6>
              <div className="mb-0">
                <label className="form-label">Personel</label>
                <Input
                  type="select"
                  value={formData.personnelId}
                  onChange={(e) => setFormData({ ...formData, personnelId: e.target.value })}
                >
                  <option value="">Personel Seçin</option>
                  {personnelList.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName} {p.title ? `(${p.title})` : ''}
                    </option>
                  ))}
                </Input>
              </div>
            </div>

            {/* İzin Detayları */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
              <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                İzin Detayları
              </h6>

              {isBulk ? (
                <Row>
                  <Col xs={6}>
                    <div className="mb-3">
                      <label className="form-label">Başlangıç</label>
                      <Input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      />
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="mb-3">
                      <label className="form-label">Bitiş</label>
                      <Input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      />
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className="mb-3">
                  <label className="form-label">Tarih</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Tür</label>
                <Input
                  type="select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="IZIN">İzin</option>
                  <option value="RAPOR">Rapor</option>
                  <option value="DEVAMSIZLIK">Devamsızlık</option>
                </Input>
              </div>

              <div className="mb-0">
                <label className="form-label">Not (opsiyonel)</label>
                <Input
                  type="textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  placeholder="Açıklama..."
                />
              </div>
            </div>

            {isBulk && (
              <div className="alert alert-info">
                <i className="ri-information-line me-1"></i>
                Seçilen tarih aralığındaki hafta içi günleri için izin kaydı oluşturulur.
                Mevcut kayıtlar atlanır.
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              <Button color="light" type="button" onClick={() => setModalOpen(false)}>İptal</Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? <Spinner size="sm" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </SideModal>
    </>
  );
};

export default PersonnelLeaveList;
